import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
    FaArrowLeft,
    FaSignOutAlt,
    FaSpinner,
    FaExclamationCircle,
    FaCheckCircle,
    FaPlus,
    FaChevronRight,
    FaChevronDown,
    FaChevronUp,
    FaLayerGroup,
    FaThList,
    FaFileAlt,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaCalendarCheck,
    FaUser,
    FaUserTie,
    FaIdBadge,
    FaUserTag,
    FaUserFriends,
    FaUserCog,
    FaEnvelope,
    FaEnvelopeOpenText,
    FaPhone,
    FaPhoneSquareAlt,
    FaBuilding,
    FaClipboard,
    FaClipboardList,
    FaUpload,
    FaTimes,
    FaSearch,
    FaFilter,
    FaGlobe,
    FaHandshake,
    FaTag,
    FaInfoCircle,
    FaHome,
    FaProjectDiagram,
    FaCommentDots,
    FaMapSigns,
    FaMoneyBillWave,
    FaLink,
    FaHardHat,
    FaWindowMaximize
} from 'react-icons/fa'
import {
    fetchBoards,
    fetchBoardColumns,
    fetchBoardItems,
    fetchAllProjects,
    clearMondayCache,
    buildMondayCacheKey,
    createItem,
    uploadFile
} from '../../lib/monday'
import { saveDraft, loadDraft, deleteDraft, addToOutbox, getOutboxCount } from '../../lib/offlineStore'
import { flushOutbox, initOfflineSync, onSyncEvent, requestNotificationPermission } from '../../lib/offlineSync'
import ServiceHealthBar from '../../components/ServiceHealthBar'
import { useVisibilityRefresh } from '../../hooks/useVisibilityRefresh'
import { syncFormToGHL } from '../../lib/ghl'
import { fetchAddressSuggestions, getPlaceDetails, newSessionToken } from '../../lib/googlePlaces'

// Per-column config: translation key + unique icon + color
// Keys match Monday.com column IDs across all boards
const FIELD_CONFIG = {
    // --- Project Info ---
    name:                { labelKey: 'admin.monday.project_address',  icon: FaMapMarkerAlt,       color: 'text-red-500' },
    location_mkt69c7k:   { labelKey: 'admin.monday.maps_location',    icon: FaMapSigns,           color: 'text-blue-500' },
    color_mktck81k:      { labelKey: 'admin.monday.project_type',       icon: FaTag,               color: 'text-amber-500' },
    color_mktcap3t:      { labelKey: 'admin.monday.project_description', icon: FaInfoCircle,        color: 'text-sky-500' },
    long_text_mkt99x5n:  { labelKey: 'admin.monday.scope_work',       icon: FaClipboardList,      color: 'text-indigo-500' },
    date4:               { labelKey: 'admin.monday.date_request',     icon: FaCalendarAlt,        color: 'text-purple-500' },
    color_mkt9jxhh:      { labelKey: 'admin.monday.office',           icon: FaBuilding,           color: 'text-emerald-600' },
    status:              { labelKey: 'admin.monday.pre_contract_status', icon: FaCheckCircle,  color: 'text-emerald-600' },
    text_mkt9xkay:       { labelKey: 'admin.monday.project_id',       icon: FaIdBadge,          color: 'text-blue-500' },
    dropdown_mkt9ed74:   { labelKey: 'admin.monday.certifying_entity', icon: FaClipboardList,   color: 'text-indigo-500' },
    // --- Applicant Info ---
    text_mktgdxcq:       { labelKey: 'admin.monday.applicant_name',   icon: FaUserTie,            color: 'text-violet-500' },
    color_mktg2qnd:      { labelKey: 'admin.monday.applicant_type',   icon: FaIdBadge,            color: 'text-orange-500' },
    // --- Client Info ---
    text_mkt9adtr:       { labelKey: 'admin.monday.client_name',      icon: FaUser,               color: 'text-blue-600' },
    dropdown_mkt96kxd:   { labelKey: 'admin.monday.client_type',      icon: FaUserTag,            color: 'text-teal-500' },
    dropdown_mkt9162r:   { labelKey: 'admin.monday.language',          icon: FaGlobe,              color: 'text-cyan-500' },
    text_mkt9qv82:       { labelKey: 'admin.monday.client_email',     icon: FaEnvelope,           color: 'text-red-400' },
    phone_mkt9n841:      { labelKey: 'admin.monday.client_phone',     icon: FaPhone,              color: 'text-green-600' },
    // --- Company / Secondary Contact ---
    color_mktg173g:      { labelKey: 'admin.monday.relationship',     icon: FaHandshake,          color: 'text-amber-600' },
    text_mktg6f8x:       { labelKey: 'admin.monday.business_name',    icon: FaUserFriends,        color: 'text-slate-500' },
    text_mktg2qkz:       { labelKey: 'admin.monday.business_email',   icon: FaEnvelopeOpenText,   color: 'text-rose-400' },
    phone_mktgvjyf:      { labelKey: 'admin.monday.business_phone',   icon: FaPhoneSquareAlt,     color: 'text-lime-600' },
    text_mm8kasxy:       { labelKey: 'admin.monday.legal_rep_name',   icon: FaUserTie,            color: 'text-indigo-600' },
    text_mm8kdbqf:       { labelKey: 'admin.monday.business_name',    icon: FaUserFriends,        color: 'text-slate-500' },
    text_mm8k828a:       { labelKey: 'admin.monday.legal_rep_name',   icon: FaUserTie,            color: 'text-indigo-600' },
    // --- Additional ---
    long_text_mktge0n8:  { labelKey: 'admin.monday.comments',         icon: FaCommentDots,        color: 'text-gray-500' },
    dropdown_mkta7bzs:   { labelKey: 'admin.monday.new_scope_type',   icon: FaProjectDiagram,     color: 'text-fuchsia-500' },
    dropdown_mktdmrak:   { labelKey: 'admin.monday.previous_pm',      icon: FaUserCog,            color: 'text-stone-500' },
    date_mkwae5gk:       { labelKey: 'admin.monday.visit_date',       icon: FaCalendarCheck,      color: 'text-green-500' },
    color_mkx0gqxh:      { labelKey: 'admin.monday.roof_type',        icon: FaHome,               color: 'text-orange-600' },
    numeric_mkt9ya3k:    { labelKey: 'admin.monday.total_contract_amount', icon: FaMoneyBillWave, color: 'text-green-600' },
    numeric_mkt9hprr:    { labelKey: 'admin.monday.first_payment_amount', icon: FaMoneyBillWave, color: 'text-green-500' },
    date_mkt96ehh:       { labelKey: 'admin.monday.first_payment_date', icon: FaCalendarAlt,    color: 'text-purple-500' },
    numeric_mkta7tj0:    { labelKey: 'admin.monday.second_payment_amount', icon: FaMoneyBillWave, color: 'text-green-500' },
    date_mktayr62:       { labelKey: 'admin.monday.second_payment_date', icon: FaCalendarAlt,   color: 'text-purple-500' },
    numeric_mktay213:    { labelKey: 'admin.monday.third_payment_amount', icon: FaMoneyBillWave, color: 'text-green-500' },
    date_mktaajar:       { labelKey: 'admin.monday.third_payment_date', icon: FaCalendarAlt,    color: 'text-purple-500' },
    numeric_mktavk9h:    { labelKey: 'admin.monday.fourth_payment_amount', icon: FaMoneyBillWave, color: 'text-green-500' },
    date_mktazx2v:       { labelKey: 'admin.monday.fourth_payment_date', icon: FaCalendarAlt,   color: 'text-purple-500' },
    long_text_mktjdvnr:  { labelKey: 'admin.monday.subcontractor_certification', icon: FaClipboardList, color: 'text-amber-600' },
    long_text_mktjfqxz:  { labelKey: 'admin.monday.subcontractor_design', icon: FaClipboardList, color: 'text-amber-500' },
    link_mktbwb:         { labelKey: 'admin.monday.dallas_link',      icon: FaLink,               color: 'text-blue-500' },
    link_mktb12zq:       { labelKey: 'admin.monday.miami_link',       icon: FaLink,               color: 'text-blue-500' },
    // --- Construction board (shared with roofing) ---
    color_mktck81k:      { labelKey: 'admin.monday.project_type',       icon: FaTag,               color: 'text-amber-500' },
    color_mktcap3t:      { labelKey: 'admin.monday.project_description', icon: FaInfoCircle,        color: 'text-sky-500' },
    // --- Shared new columns (Construction + Roofing) ---
    long_text_mm1k8fb1:  { labelKey: 'admin.monday.scope_work',     icon: FaClipboardList,      color: 'text-indigo-500' },
    text_mm1gpb79:       { labelKey: 'admin.monday.applicant_name',  icon: FaUser,               color: 'text-purple-500' },
    color_mm1gktv9:      { labelKey: 'admin.monday.applicant_type',  icon: FaUserTag,            color: 'text-purple-400' },
    text_mm1ged6r:       { labelKey: 'admin.monday.applicant_name',  icon: FaUser,               color: 'text-purple-500' },
    color_mm1gcjer:      { labelKey: 'admin.monday.applicant_type',  icon: FaUserTag,            color: 'text-purple-400' },
    color_mm1mdv85:      { labelKey: 'admin.monday.option_subcategory', icon: FaTag,             color: 'text-sky-400' }
}

// Fallback type-level icons (for admin mode / unmapped columns)
const TYPE_FALLBACK_ICONS = {
    text: { icon: FaFileAlt, color: 'text-ash' },
    long_text: { icon: FaClipboard, color: 'text-ash' },
    numbers: { icon: FaFileAlt, color: 'text-blue-500' },
    numeric: { icon: FaFileAlt, color: 'text-blue-500' },
    status: { icon: FaCheckCircle, color: 'text-green-500' },
    color: { icon: FaCheckCircle, color: 'text-green-500' },
    date: { icon: FaCalendarAlt, color: 'text-purple-500' },
    dropdown: { icon: FaThList, color: 'text-indigo-500' },
    email: { icon: FaEnvelope, color: 'text-red-400' },
    phone: { icon: FaPhone, color: 'text-green-600' },
    location: { icon: FaMapMarkerAlt, color: 'text-red-500' },
    link: { icon: FaLink, color: 'text-blue-500' }
}

// Type options for color_mktck81k on the Construction board (Tipo column)
// These map to Monday status-column label indices: 0=Comercial, 1=Residencial
// Civil will be sent as label text (Monday auto-creates the index)
const PROJECT_TYPE_OPTIONS = [
    { value: '0',    label: 'Comercial' },
    { value: '1',    label: 'Residencial' },
    { value: 'Civil', label: 'Civil' }
]

// WorkForm configurations - matches existing Monday.com WorkForms
// visibleColumns: ordered list of column IDs to show
const WORK_FORMS = {
    estimados_2026_new_app: {
        id: 'estimados_2026_new_app',
        boardId: '18396590426',
        boardName: 'Estimados 2026',
        formName: 'New Application',
        groupId: 'group_title',
        description: 'New architecture project application',
        visibleColumns: [
            'name', 'location_mkt69c7k', 'color_mktck81k', 'color_mktcap3t',
            'long_text_mkt99x5n', 'date4', 'text_mktgdxcq', 'color_mktg2qnd', 'color_mkt9jxhh',
            'text_mkt9adtr', 'dropdown_mkt96kxd', 'dropdown_mkt9162r', 'text_mkt9qv82',
            'phone_mkt9n841', 'text_mktg6f8x', 'text_mm8kasxy', 'file_mkt9257p'
        ]
    },
    estimados_2026_new_scope: {
        id: 'estimados_2026_new_scope',
        boardId: '18396590426',
        boardName: 'Estimados 2026',
        formName: 'New Scope',
        groupId: 'group_mkthpgtb',
        description: 'Scope expansion for existing projects',
        visibleColumns: [
            'name', 'location_mkt69c7k', 'color_mktck81k', 'color_mktcap3t', 'long_text_mkt99x5n',
            'date4', 'text_mktgdxcq', 'color_mktg2qnd', 'color_mkt9jxhh',
            'text_mkt9adtr', 'dropdown_mkt96kxd', 'dropdown_mkt9162r',
            'text_mkt9qv82', 'phone_mkt9n841', 'text_mktg6f8x', 'text_mm8kasxy', 'file_mkt9257p',
            'dropdown_mkta7bzs', 'dropdown_mktdmrak'
        ]
    },
    construction_new_project: {
        id: 'construction_new_project',
        boardId: '18157237865',
        boardName: 'Estimados 2026 (Construction)',
        formName: 'Construction Intake',
        groupId: 'group_mkt9qs2f',
        description: 'Unify residential, commercial, and civil intake',
        visibleColumns: [
            'name', 'location_mkt69c7k', 'color_mktck81k', 'color_mktcap3t', '_option_subcategory',
            'long_text_mm1k8fb1', 'date_mkwae5gk', 'text_mm1gpb79', 'color_mm1gktv9',
            'text_mkt9adtr', 'dropdown_mkt96kxd',
            'dropdown_mkt9162r', 'text_mkt9qv82', 'phone_mkt9n841',
            'text_mm8kdbqf', 'text_mm8k828a'
        ]
    },
    roofing_new_project: {
        id: 'roofing_new_project',
        boardId: '18157237865',
        boardName: 'Estimados 2026 (Construction)',
        formName: 'Roofing Intake',
        groupId: 'group_mkt9qs2f',
        visibleColumns: [
            'name', 'location_mkt69c7k', 'color_mktck81k', 'color_mktcap3t', '_option_subcategory',
            'long_text_mm1k8fb1', 'date_mkwae5gk', 'text_mm1gpb79', 'color_mm1gktv9',
            'text_mkt9adtr', 'dropdown_mkt96kxd',
            'dropdown_mkt9162r', 'text_mkt9qv82', 'phone_mkt9n841',
            'text_mm8kdbqf', 'text_mm8k828a'
        ]
    },
    impact_windows_new_project: {
        id: 'impact_windows_new_project',
        boardId: '18157237865',
        boardName: 'Estimados 2026 (Construction)',
        formName: 'Impact Windows Intake',
        groupId: 'group_mkt9qs2f',
        visibleColumns: [
            'name', 'location_mkt69c7k', 'color_mktck81k', 'color_mktcap3t', '_option_subcategory',
            'long_text_mm1k8fb1', 'date_mkwae5gk', 'text_mm1gpb79', 'color_mm1gktv9',
            'text_mkt9adtr', 'dropdown_mkt96kxd',
            'dropdown_mkt9162r', 'text_mkt9qv82', 'phone_mkt9n841',
            'text_mm8kdbqf', 'text_mm8k828a'
        ]
    }
}

// Client picker: identity columns autofilled when an existing client is selected.
// Only columns also present in the form's visibleColumns are written.
const CLIENT_PICKER_COLS = {
    '18396590426': ['text_mkt9adtr', 'text_mkt9qv82', 'phone_mkt9n841', 'text_mktg6f8x', 'text_mm8kasxy', 'dropdown_mkt96kxd', 'dropdown_mkt9162r'],
    '18157237865': ['text_mkt9adtr', 'text_mkt9qv82', 'phone_mkt9n841', 'text_mm8kdbqf', 'text_mm8k828a', 'dropdown_mkt96kxd', 'dropdown_mkt9162r'],
}
const CLIENT_PICKER_NAME_COL = 'text_mkt9adtr'
const CLIENT_PICKER_LOCATION_COL = 'location_mkt69c7k'
const CLIENT_PICKER_PHONE_COLS = new Set(['phone_mkt9n841', 'phone_mktgvjyf'])

function getMonColText(item, colId) {
    return item.column_values?.find(c => c.id === colId)?.text || ''
}

// Status colors from the form
const STATUS_COLORS = {
    // Type labels (color_mktck81k)
    'Comercial': '#fdab3d',
    'Residencial': '#579bfc',
    'Civil': '#c4c4c4',
    
    // Description categories (color_mktcap3t)
    'New Construction': '#579bfc',
    'Addition': '#fdab3d',
    'Remodel': '#9cd626',
    'Re-Roof': '#e44258',
    'Windows': '#00c875',
    'Pergola': '#9cd626',
    'Concrete': '#c4c4c4',
    
    // Subcategories for Nueva construcción
    'House': '#579bfc',
    'ADU': '#fdab3d',
    'Storage': '#9cd626',
    
    // Subcategories for Addition
    'Attached': '#e44258',
    'Attached Aluminium Patio Cover': '#00c875',
    'Detached ADU': '#9cd626',
    'Detached Efficiency': '#c4c4c4',
    'Detached Storage': '#579bfc',
    
    // Subcategories for Remodel
    'Interior': '#579bfc',
    'Exterior': '#fdab3d',
    
    // Subcategories for Re-Roof
    'Clay Tiles': '#e44258',
    'Concrete Tiles': '#fdab3d',
    'Metal': '#00c875',
    'Shingle': '#9cd626',
    'Flat': '#579bfc',
    'Mixto': '#c4c4c4',
    
    // Category: Fence
    'Fence': '#7e5bef',
    
    // Subcategories for Fence
    'Dura Fence': '#579bfc',
    'Wood Fence': '#fdab3d',
    'PVC': '#66ccff',
    
    // Subcategories for Windows
    'Single Hung': '#e44258',
    'Horizontal Roller': '#00c875',
    
    // Subcategories for Pergola
    'Aluminio': '#9cd626',
    'Tradicional': '#c4c4c4',
    
    // Subcategories for Concrete
    'Driveway': '#579bfc',
    'Parking': '#fdab3d',
    'Street': '#9cd626',
    
    // Common subcategory
    'Other': '#e44258',
    
    // Oficina (color_mkt9jxhh)
    'Miami 2026': '#00c875',
    'Dallas 2026': '#e44258',
    
    // Tipo solicitante (color_mktg2qnd)
    'Cliente': '#225092',
    'Equipo de ventas': '#00c875',
    
    // Relacion empresa (color_mktg173g)
    'Cliente existente': '#225092',
    'Cliente nuevo': '#9d99b9',
}

const MondayManagement = ({ session, onReturn, onLogout, isPublicMode = false, defaultFormType = null, initialPreselect = null, thankYouPath = null, utmData = null }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const lockedFields = new Set()
    if (initialPreselect?.type) lockedFields.add('color_mktck81k')
    if (initialPreselect?.desc) lockedFields.add('color_mktcap3t')

    const isLandingMode = !!initialPreselect

    // Build UTM query string for thank-you page redirect
    const utmQuery = utmData ? Object.entries(utmData).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&') : ''

    // Landing forms: reduced field set with custom order
    // Type + Description are hidden but still seeded in formData via initialPreselect
    const LANDING_VISIBLE_COLUMNS = [
        '_option_subcategory',
        '_landing_timeline',
        'text_mkt9adtr', 'phone_mkt9n841', 'text_mkt9qv82',
        '_landing_address_row', 'long_text_mm1k8fb1'
    ]

    // Boards browser hidden by product decision — keep the code path so it
    // can be re-enabled later; the landing view shows only the form cards.
    const SHOW_BOARDS = false

    // View states
    const [view, setView] = useState(isPublicMode ? 'create' : 'boards') // 'boards', 'items', 'create'

    // Data states
    const [boards, setBoards] = useState([])
    const [selectedBoard, setSelectedBoard] = useState(null)
    const [boardColumns, setBoardColumns] = useState([])
    const [items, setItems] = useState([])
    const [cursor, setCursor] = useState(null)
    const [hasMore, setHasMore] = useState(false)

    // UI states
    const [loading, setLoading] = useState(true)
    const [loadingItems, setLoadingItems] = useState(false)
    const [error, setError] = useState(null)
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' })
    const [successModal, setSuccessModal] = useState({ show: false, message: '' })

    // Search & filter
    const [searchTerm, setSearchTerm] = useState('')
    const [expandedItems, setExpandedItems] = useState(new Set())

    // Form states
    const [formData, setFormData] = useState({})
    const [uploadingFiles, setUploadingFiles] = useState([])
    const [submitting, setSubmitting] = useState(false)
    const [activeFormType, setActiveFormType] = useState(null) // Which WorkForm is being used
    const fileInputRef = useRef(null)
    const [locationSuggestions, setLocationSuggestions] = useState({})
    const locationDebounceRef = useRef({})

    // Client picker (authenticated only — never in public/landing forms)
    const [clientSuggestions, setClientSuggestions] = useState([])
    const [clientItemsCache, setClientItemsCache] = useState({}) // boardId -> items[]
    const clientDebounceRef = useRef(null)

    // Offline / PWA states
    const [isOnline, setIsOnline] = useState(navigator.onLine)
    const [draftPrompt, setDraftPrompt] = useState(null) // { formData, files, savedAt } or null
    const [outboxCount, setOutboxCount] = useState(0)
    const draftTimerRef = useRef(null)
    const draftUserId = session?.user?.id || null

    // Dynamic subcategories based on main category
    const SUBCATEGORY_MAP = {
        'New Construction': ['House', 'ADU', 'Storage', 'Other'],
        'Addition': ['Attached', 'Attached Aluminium Patio Cover', 'Detached ADU', 'Detached Efficiency', 'Detached Storage', 'Other'],
        'Remodel': ['Interior', 'Exterior', 'Other'],
        'Re-Roof': ['Clay Tiles', 'Concrete Tiles', 'Metal', 'Shingle', 'Flat', 'Mixto', 'Other'],
        'Windows': ['Single Hung', 'Horizontal Roller', 'Other'],
        'Pergola': ['Aluminio', 'Tradicional', 'Other'],
        'Concrete': ['Driveway', 'Parking', 'Street', 'Other'],
        'Fence': ['Dura Fence', 'Wood Fence', 'PVC', 'Other']
    }

    // Landing-specific subcategory overrides
    const LANDING_SUBCATEGORY_MAP = {
        'Concrete': ['Concrete | Driveway', 'Concrete | Patio', 'Natural grass', 'Artificial grass'],
        'Pergola': ['Pergola | Classic', 'Pergola | Modern'],
        'Re-Roof': ['Roof | Clay tile', 'Roof | Concrete tile', 'Roof | Metal', 'Roof | Shingle', 'Roof | Flat', 'Roof | Mixed', 'Roof | Other'],
    }

    // Translation key mapping for categories and subcategories
    const getTranslationKey = (text) => {
        const keyMap = {
            // Main categories
            'New Construction': 'nueva_construccion',
            'Addition': 'addition',
            'Remodel': 'remodel',
            'Re-Roof': 're_roof',
            'Windows': 'windows',
            'Pergola': 'pergola',
            'Concrete': 'concrete',
            'Fence': 'cerca',
            // Subcategories
            'House': 'house',
            'ADU': 'adu',
            'Storage': 'storage',
            'Attached': 'attached',
            'Attached Aluminium Patio Cover': 'attached_aluminium_patio_cover',
            'Detached ADU': 'detached_adu',
            'Detached Efficiency': 'detached_efficiency',
            'Detached Storage': 'detached_storage',
            'Interior': 'interior',
            'Exterior': 'exterior',
            'Clay Tiles': 'clay_tiles',
            'Concrete Tiles': 'concrete_tiles',
            'Metal': 'metal',
            'Shingle': 'shingle',
            'Flat': 'flat',
            'Mixto': 'mixto',
            'Single Hung': 'single_hung',
            'Horizontal Roller': 'horizontal_roller',
            'Aluminio': 'aluminio',
            'Tradicional': 'tradicional',
            'Driveway': 'driveway',
            'Parking': 'parking',
            'Street': 'street',
            'Dura Fence': 'dura_fence',
            'Wood Fence': 'wood_fence',
            'PVC': 'pvc',
            'Patio': 'patio',
            'Turf': 'turf',
            'All': 'all',
            'Classic with Overhang': 'classic_with_overhang',
            'Modern Flat': 'modern_flat',
            'Mixed': 'mixed',
            'Other': 'other',
            'Roof | Clay tile': 'roof_clay_tile',
            'Roof | Concrete tile': 'roof_concrete_tile',
            'Roof | Metal': 'roof_metal',
            'Roof | Shingle': 'roof_shingle',
            'Roof | Flat': 'roof_flat',
            'Roof | Mixed': 'roof_mixed',
            'Roof | Other': 'roof_other',
            'Concrete | Driveway': 'concrete_driveway',
            'Concrete | Patio': 'concrete_patio',
            'Natural grass': 'natural_grass',
            'Artificial grass': 'artificial_grass',
            'Pergola | Classic': 'pergola_classic',
            'Pergola | Modern': 'pergola_modern'
        }
        return keyMap[text] || text.toLowerCase().replace(/\s+/g, '_')
    }

    // Fetch boards on mount (only in admin mode)
    useEffect(() => {
        if (!isPublicMode) {
            loadBoards()
        }
    }, [isPublicMode])

    const markMondayRefreshedRef = useRef(null)

    // Silent background refresh when tab becomes visible (admin mode, guard when form open)
    const { markRefreshed: markMondayRefreshed } = useVisibilityRefresh({
        staleMs: 300_000,
        onRefresh: useCallback(() => {
            if (isPublicMode || view === 'create') return
            const fetches = [loadBoards(true)]
            if (selectedBoard && view === 'items') {
                fetches.push(selectBoard(selectedBoard, true))
            }
            Promise.allSettled(fetches).finally(() => markMondayRefreshedRef.current?.())
        }, [isPublicMode, view, selectedBoard]),
    })
    markMondayRefreshedRef.current = markMondayRefreshed

    // Auto-open form in public mode
    useEffect(() => {
        if (isPublicMode) {
            // Set loading to false immediately for public mode
            setLoading(false)
            // Auto-open the default form (Architecture by default)
            openCreateForm(defaultFormType || 'estimados_2026_new_app')
        }
    }, [isPublicMode, defaultFormType])

    // Cleanup per-field location debounce timers on unmount
    useEffect(() => {
        return () => {
            Object.values(locationDebounceRef.current).forEach(clearTimeout)
        }
    }, [])

    // ── Online/offline tracking + sync initialization ──
    useEffect(() => {
        const goOnline = () => setIsOnline(true)
        const goOffline = () => setIsOnline(false)
        window.addEventListener('online', goOnline)
        window.addEventListener('offline', goOffline)

        // Initialize offline sync (listens for SW messages + online event)
        initOfflineSync(isPublicMode ? null : session)

        // Listen for sync events to update outbox count
        const unsub = onSyncEvent(async (evt) => {
            if (evt.type === 'sync_success' || evt.type === 'sync_done') {
                const count = await getOutboxCount()
                setOutboxCount(count)
                if (evt.type === 'sync_success') {
                    showToast(t('admin.monday.queued_form_sent', 'A queued form has been submitted successfully'), 'success')
                }
            }
        })

        // Initial outbox count
        getOutboxCount().then(setOutboxCount).catch(() => {})

        return () => {
            window.removeEventListener('online', goOnline)
            window.removeEventListener('offline', goOffline)
            unsub()
        }
    }, [])

    // ── Draft auto-save (debounced 1.5s after last change) ──
    const LANDING_SEED_KEYS = new Set(['color_mktck81k', 'color_mktcap3t', 'date_mkwae5gk', 'dropdown_mkt9162r'])
    useEffect(() => {
        if (!activeFormType || view !== 'create') return
        // Don't save empty forms — in landing mode, ignore auto-seeded fields
        const hasData = Object.entries(formData).some(([k, v]) => {
            if (v === '' || v === null || v === undefined) return false
            if (isLandingMode && LANDING_SEED_KEYS.has(k)) return false
            if (k === 'name' && v === '') return false
            return true
        })
        if (!hasData) return

        if (draftTimerRef.current) clearTimeout(draftTimerRef.current)
        draftTimerRef.current = setTimeout(() => {
            saveDraft(activeFormType, formData, uploadingFiles, draftUserId).catch(err =>
                console.warn('[Draft] Save error:', err)
            )
        }, 1500)

        return () => {
            if (draftTimerRef.current) clearTimeout(draftTimerRef.current)
        }
    }, [formData, uploadingFiles, activeFormType, view])

    // Flush pending draft to IndexedDB before tab close / hide (public forms)
    useEffect(() => {
        if (!isPublicMode || !activeFormType) return
        let flushing = false
        const flush = () => {
            if (flushing || !draftTimerRef.current) return
            flushing = true
            clearTimeout(draftTimerRef.current)
            draftTimerRef.current = null
            saveDraft(activeFormType, formData, uploadingFiles, draftUserId).catch(() => {})
        }
        window.addEventListener('beforeunload', flush)
        const onVis = () => { if (document.visibilityState === 'hidden') flush() }
        document.addEventListener('visibilitychange', onVis)
        return () => {
            window.removeEventListener('beforeunload', flush)
            document.removeEventListener('visibilitychange', onVis)
        }
    }, [isPublicMode, activeFormType, formData, uploadingFiles, draftUserId])

    async function loadBoards(silent = false) {
        if (!silent) {
            setLoading(true)
            setError(null)
        }
        try {
            const cached = sessionStorage.getItem('monday_boards_cache')
            if (cached) {
                setBoards(JSON.parse(cached))
                if (!silent) setLoading(false)
            }
            const data = await fetchBoards(session)
            setBoards(data || [])
            sessionStorage.setItem('monday_boards_cache', JSON.stringify(data || []))
        } catch (err) {
            console.error('Error fetching boards:', err)
            if (!silent) {
                setError(err.message || 'Failed to fetch boards')
                showToast(err.message || 'Failed to fetch boards', 'error')
            }
        } finally {
            if (!silent) setLoading(false)
        }
    }

    async function selectBoard(board, silent = false) {
        setSelectedBoard(board)
        setView('items')
        if (!silent) setLoadingItems(true)
        setItems([])
        setCursor(null)
        setExpandedItems(new Set())

        try {
            // Fetch columns and items in parallel
            const [columnsData, itemsData] = await Promise.all([
                fetchBoardColumns(board.id, session),
                fetchBoardItems(board.id, 50, null, session)
            ])

            setBoardColumns(columnsData?.columns || [])
            setItems(itemsData.items || [])
            setCursor(itemsData.cursor)
            setHasMore(!!itemsData.cursor)
        } catch (err) {
            console.error('Error fetching board data:', err)
            showToast(err.message || 'Failed to fetch board data', 'error')
        } finally {
            if (!silent) setLoadingItems(false)
        }
    }

    async function loadMoreItems() {
        if (!cursor || loadingItems) return

        setLoadingItems(true)
        try {
            const data = await fetchBoardItems(selectedBoard.id, 50, cursor, session)
            setItems(prev => [...prev, ...data.items])
            setCursor(data.cursor)
            setHasMore(!!data.cursor)
        } catch (err) {
            console.error('Error loading more items:', err)
            showToast(err.message || 'Failed to load more items', 'error')
        } finally {
            setLoadingItems(false)
        }
    }

    function toggleItemExpand(itemId) {
        setExpandedItems(prev => {
            const newSet = new Set(prev)
            if (newSet.has(itemId)) {
                newSet.delete(itemId)
            } else {
                newSet.add(itemId)
            }
            return newSet
        })
    }

    function showToast(message, type = 'success') {
        setToast({ show: true, message, type })
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000)
    }

    // Filter items by search term
    const filteredItems = items.filter(item => {
        if (!searchTerm) return true
        const search = searchTerm.toLowerCase()
        const name = (item.name || '').toLowerCase()
        const columnText = item.column_values?.map(cv => cv.text || '').join(' ').toLowerCase()
        return name.includes(search) || columnText.includes(search)
    })

    // Filter boards by search term
    const filteredBoards = boards.filter(board => {
        if (!searchTerm) return true
        const search = searchTerm.toLowerCase()
        return (board.name || '').toLowerCase().includes(search) ||
            (board.description || '').toLowerCase().includes(search)
    })
    // --- DYNAMIC FORM HELPERS ---

    // Shared input styles
    const inputBase = 'w-full px-4 py-3 bg-white rounded-lg border border-gray-200 text-ink text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all'
    const labelBase = 'flex items-center gap-2 mb-2 text-sm font-medium text-ink'
    const fieldWrapper = 'mb-6'

    // MaxLength configuration — sensible real-world limits
    const FIELD_MAX_LENGTHS = {
        'name': 100,              // Project address
        'text_mktgdxcq': 60,     // Applicant name
        'text_mkt9adtr': 60,     // Client name
        'text_mm1gpb79': 60,     // Applicant name (Construction/Roofing)
        'text_mm1ged6r': 60,     // Applicant name (alt)
        'text_mkt9qv82': 80,     // Client email
        'text_mktg2qkz': 80,    // Business email
        'phone_mkt9n841': 15,    // Client phone (raw digits)
        'phone_mktgvjyf': 15,   // Business phone (raw digits)
        'location_mkt69c7k': 200, // Location address
        'long_text_mkt99x5n': 1000, // Scope description (Architecture)
        'long_text_mm1k8fb1': 1000, // Scope description (Construction)
        'long_text_mktge0n8': 500,  // Comments
        'link_mktbwb': 300,      // Dallas link
        'link_mktb12zq': 300,    // Miami link
        'text_mktg6f8x': 80,    // Business name
        'text_mm8kasxy': 80,   // Legal rep name (Estimates)
        'text_mm8kdbqf': 80,   // Company name (Construction)
        'text_mm8k828a': 80,   // Legal rep name (Construction)
    }

    // Column IDs that are semantically email fields (Monday stores them as 'text' type)
    const EMAIL_FIELD_IDS = new Set(['text_mkt9qv82', 'text_mktg2qkz'])

    // Fields required in public (form) mode
    const REQUIRED_PUBLIC_FIELDS = isLandingMode
        ? new Set(['color_mktck81k', 'color_mktcap3t', 'text_mkt9adtr', 'phone_mkt9n841', '_landing_zip'])
        : new Set(['name', 'color_mktck81k', 'color_mktcap3t', 'text_mkt9adtr', 'text_mkt9qv82', 'phone_mkt9n841'])

    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    // Client picker — authenticated sessions only; public/landing forms must never
    // expose existing client data
    const canPickClients = !isLandingMode && !isPublicMode && !!session

    const ensureClientItems = useCallback(async (boardId) => {
        if (clientItemsCache[boardId]) return clientItemsCache[boardId]
        try {
            const raw = await fetchAllProjects([boardId], session)
            const items = raw.map(({ item }) => item).filter(i => getMonColText(i, CLIENT_PICKER_NAME_COL))
            setClientItemsCache(prev => ({ ...prev, [boardId]: items }))
            return items
        } catch { return [] }
    }, [clientItemsCache, session])

    const runClientFilter = useCallback(async (text, boardId) => {
        const q = text.trim().toLowerCase()
        if (q.length < 2 || !boardId) { setClientSuggestions([]); return }
        const items = await ensureClientItems(boardId)
        const seen = new Set()
        const out = []
        for (const i of items) {
            const name = getMonColText(i, CLIENT_PICKER_NAME_COL)
            if (!name.toLowerCase().includes(q)) continue
            // Dedup by name+email — same client across projects collapses,
            // distinct people sharing a name stay separate suggestions
            const key = name.trim().toLowerCase() + '|' + getMonColText(i, 'text_mkt9qv82').toLowerCase()
            if (seen.has(key)) continue
            seen.add(key)
            out.push(i)
            if (out.length >= 8) break
        }
        setClientSuggestions(out)
    }, [ensureClientItems])

    const handleClientSelect = (item) => {
        const formConfig = WORK_FORMS[activeFormType]
        const cols = CLIENT_PICKER_COLS[formConfig?.boardId] || []
        const visible = new Set(formConfig?.visibleColumns || [])
        setFormData(prev => {
            const next = { ...prev }
            for (const col of cols) {
                if (!visible.has(col)) continue
                let v = getMonColText(item, col)
                if (!v) continue
                if (col.startsWith('dropdown_')) {
                    // Only autofill values that exist as a board option — never create stray labels
                    let names = []
                    try { names = (JSON.parse(boardColumns.find(c => c.id === col)?.settings_str || '{}').labels || []).map(l => l.name) } catch { /* no options */ }
                    if (!names.includes(v)) continue
                }
                if (CLIENT_PICKER_PHONE_COLS.has(col)) {
                    v = v.replace(/\D/g, '')
                    if (v.length > 10 && v.startsWith('1')) v = v.slice(1)
                }
                next[col] = v
            }
            return next
        })
        setClientSuggestions([])

        // Address fields: `name` (project address, string — feeds item name) and
        // the location column ({lat, lng, address} — plain text is skipped at submit).
        // Geocode the stored address for accurate coords (Monday values often carry
        // placeholder coordinates); fall back to the coordinates stored in Monday.
        const cv = item.column_values?.find(c => c.id === CLIENT_PICKER_LOCATION_COL)
        const addr = cv?.text || (item.name?.includes('|') ? item.name.split('|').pop().trim() : '')
        if (addr && visible.has('name')) handleFormChange('name', addr)
        if (addr && visible.has(CLIENT_PICKER_LOCATION_COL)) {
            fetchAddressSuggestions(addr)
                .then(async suggs => {
                    const details = suggs?.[0]?.placeId ? await getPlaceDetails(suggs[0].placeId) : null
                    if (details?.lat != null && details?.lng != null) {
                        handleFormChange(CLIENT_PICKER_LOCATION_COL, { lat: details.lat, lng: details.lng, address: details.address })
                        return
                    }
                    let v = null
                    try {
                        const parsed = JSON.parse(cv?.value || '{}')
                        if (parsed.lat != null && parsed.lng != null) v = { lat: parsed.lat, lng: parsed.lng, address: parsed.address || addr }
                    } catch { /* no stored coords */ }
                    if (v) handleFormChange(CLIENT_PICKER_LOCATION_COL, v)
                })
                .catch(() => {})
        }
    }

    // Navigation happens on close so the confirmation is actually visible
    // (the modal lives inside the 'create' view)
    const closeSuccessModal = () => {
        setSuccessModal({ show: false, message: '' })
        if (isPublicMode) { if (onReturn) onReturn(); return }
        setFormData({})
        setUploadingFiles([])
        setView('items')
        if (selectedBoard) selectBoard(selectedBoard)
    }

    const renderDynamicField = (column) => {
        const id = column.id
        const type = column.type

        // Exclusion list - don't show technical or read-only columns
        const excludedTypes = ['formula', 'lookup', 'board_relation', 'button', 'dependency', 'pulse-id', 'last-updated', 'subtasks', 'doc', 'file', 'multiple-person', 'people']
        if (excludedTypes.includes(type)) return null

        // Resolve label: landing overrides → FIELD_CONFIG translation → fallback to Monday column title
        const fieldCfg = FIELD_CONFIG[id]
        let label = fieldCfg ? t(fieldCfg.labelKey, column.title) : column.title
        if (isLandingMode) {
            if (id === 'text_mkt9adtr') label = t('admin.monday.landing_full_name', 'Full Name')
            else if (id === 'long_text_mm1k8fb1') label = t('admin.monday.landing_comments', 'Comments')
        }

        // Resolve icon: FIELD_CONFIG per-column icon → TYPE_FALLBACK_ICONS → generic
        const fallback = TYPE_FALLBACK_ICONS[type] || { icon: FaFileAlt, color: 'text-ash' }
        const FieldIcon = fieldCfg?.icon || fallback.icon
        const iconColor = fieldCfg?.color || fallback.color

        // Name field (project address) - always required in public mode
        // In landing mode, name is rendered inside _landing_address_row synthetic field
        if (id === 'name') {
            if (isLandingMode) return null
            const nameSuggestions = locationSuggestions['name'] || []
            return (
                <div key="name" className={fieldWrapper}>
                    <label className={labelBase}>
                        <FieldIcon className={iconColor} />
                        {label} <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            value={formData.name || ''}
                            onChange={(e) => handleLocationInput('name', e.target.value)}
                            onFocus={() => newSessionToken()}
                            className={inputBase}
                            placeholder={activeFormType === 'roofing_new_project' ? '0000 NW 1st Ave, Miami, FL 33000' : 'Enter project address'}
                            maxLength={FIELD_MAX_LENGTHS.name}
                            required={isPublicMode}
                            autoComplete="off"
                        />
                        {nameSuggestions.length > 0 && (
                            <ul className="absolute z-20 w-full mt-2 overflow-hidden rounded-xl border border-gray-700 bg-[#0f1115] shadow-2xl">
                                {nameSuggestions.map(suggestion => (
                                    <li
                                        key={suggestion.id}
                                        onMouseDown={(e) => {
                                            e.preventDefault()
                                            handleLocationSuggestionSelect('name', suggestion)
                                        }}
                                        className="px-4 py-3 text-sm text-white cursor-pointer hover:bg-white/5"
                                    >
                                        <p className="font-semibold">{suggestion.label}</p>
                                        {suggestion.secondary && (
                                            <p className="text-xs text-gray-400">{suggestion.secondary}</p>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )
        }

        switch (type) {
            case 'text':
            case 'numeric':
            case 'numbers': {
                const isEmail = EMAIL_FIELD_IDS.has(id)
                const maxLength = FIELD_MAX_LENGTHS[id]
                const showCharCount = maxLength && maxLength > 100
                const currentLength = (formData[id] || '').length
                const inputType = isEmail ? 'email' : (type === 'numeric' || type === 'numbers') ? 'number' : 'text'
                const isClientField = id === CLIENT_PICKER_NAME_COL && canPickClients

                return (
                    <div key={id} className={fieldWrapper}>
                        <label className={labelBase}>
                            <FieldIcon className={iconColor} />
                            {label}
                            {isPublicMode && REQUIRED_PUBLIC_FIELDS.has(id) && <span className="text-red-400">*</span>}
                            {showCharCount && (
                                <span className="ml-auto text-xs text-gray-500">
                                    {currentLength}/{maxLength}
                                </span>
                            )}
                        </label>
                        <div className="relative">
                            <input
                                type={inputType}
                                value={formData[id] || ''}
                                onChange={(e) => {
                                    handleFormChange(id, e.target.value)
                                    if (isClientField) {
                                        clearTimeout(clientDebounceRef.current)
                                        const boardId = WORK_FORMS[activeFormType]?.boardId
                                        clientDebounceRef.current = setTimeout(() => runClientFilter(e.target.value, boardId), 300)
                                    }
                                }}
                                onBlur={() => { if (isClientField) setTimeout(() => setClientSuggestions([]), 150) }}
                                className={inputBase}
                                placeholder={isEmail ? 'email@example.com' : undefined}
                                maxLength={maxLength}
                                required={isPublicMode && REQUIRED_PUBLIC_FIELDS.has(id)}
                                minLength={isEmail ? 5 : 2}
                                autoComplete={isClientField ? 'off' : undefined}
                            />
                            {isClientField && clientSuggestions.length > 0 && (
                                <ul className="overflow-hidden absolute z-20 mt-2 w-full bg-white rounded-xl border border-gray-200 shadow-2xl">
                                    {clientSuggestions.map(item => {
                                        const clientName = getMonColText(item, CLIENT_PICKER_NAME_COL)
                                        const itemAddr = item.name?.includes('|') ? item.name.split('|').pop().trim() : ''
                                        const email = getMonColText(item, 'text_mkt9qv82')
                                        const secondary = [itemAddr, email].filter(Boolean).join(' · ')
                                        return (
                                            <li
                                                key={item.id}
                                                onMouseDown={(e) => {
                                                    e.preventDefault()
                                                    handleClientSelect(item)
                                                }}
                                                className="px-4 py-3 text-sm cursor-pointer text-ink hover:bg-gray-50"
                                            >
                                                <p className="font-semibold">{clientName}</p>
                                                {secondary && (
                                                    <p className="text-xs text-gray-400">{secondary}</p>
                                                )}
                                            </li>
                                        )
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                )
            }
            case 'long_text':
                const longTextMaxLength = FIELD_MAX_LENGTHS[id]
                const longTextCurrentLength = (formData[id] || '').length
                
                return (
                    <div key={id} className={fieldWrapper}>
                        <label className={labelBase}>
                            <FieldIcon className={iconColor} />
                            {label}
                            <span className="ml-auto text-xs text-gray-500">
                                {longTextCurrentLength}/{longTextMaxLength}
                            </span>
                        </label>
                        <textarea
                            value={formData[id] || ''}
                            onChange={(e) => handleFormChange(id, e.target.value)}
                            className={`resize-y ${inputBase} min-h-[100px]`}
                            placeholder={(isLandingMode && id === 'long_text_mm1k8fb1') ? t('admin.monday.landing_comments_placeholder') : (id === 'long_text_mkt99x5n' || id === 'long_text_mm1k8fb1') ? t('admin.monday.scope_placeholder') : id === 'long_text_mktge0n8' ? t('admin.monday.comments_placeholder') : ''}
                            maxLength={longTextMaxLength}
                        />
                    </div>
                )
            case 'color':
            case 'status':
            case 'dropdown': {
                let options = []
                
                const isConstructionBoard = WORK_FORMS[activeFormType]?.boardId === '18157237865'

                if (id === 'color_mktck81k') {
                    if (isConstructionBoard) {
                        // Construction/Roofing: show Type (Residencial/Comercial/Civil) by index
                        options = PROJECT_TYPE_OPTIONS.map(opt => ({
                            value: opt.value,
                            label: t(`admin.monday.${opt.label.toLowerCase()}`, opt.label)
                        }))
                    } else {
                        // Architecture: use Monday board labels
                        try {
                            const settings = JSON.parse(column.settings_str || '{}')
                            options = Object.entries(settings.labels || {}).filter(([, label]) => label?.trim()).map(([index, label]) => ({ value: String(index), label }))
                        } catch (e) {}
                    }
                } else if (id === 'color_mktcap3t') {
                    if (isConstructionBoard) {
                        // Construction/Roofing: show Description categories (SUBCATEGORY_MAP keys)
                        options = Object.keys(SUBCATEGORY_MAP).map(cat => ({
                            value: cat,
                            label: t(`admin.monday.${getTranslationKey(cat)}`, cat)
                        }))
                    } else {
                        // Architecture: use Monday board labels
                        try {
                            const settings = JSON.parse(column.settings_str || '{}')
                            options = Object.entries(settings.labels || {}).filter(([, label]) => label?.trim()).map(([index, label]) => ({ value: String(index), label }))
                        } catch (e) {}
                    }
                } else {
                    try {
                        const settings = JSON.parse(column.settings_str || '{}')
                        if (type === 'color' || type === 'status') {
                            options = Object.entries(settings.labels || {}).filter(([, label]) => label?.trim()).map(([index, label]) => ({ value: String(index), label }))
                        } else {
                            options = (settings.labels || []).map(opt => ({ value: opt.name, label: opt.name }))
                        }
                    } catch (e) {}
                }

                return (
                    <div key={id} className={fieldWrapper}>
                        <label className={labelBase}>
                            <FieldIcon className={iconColor} />
                            {label}
                            {isPublicMode && REQUIRED_PUBLIC_FIELDS.has(id) && <span className="text-red-400">*</span>}
                        </label>
                        <select
                            value={formData[id] || ''}
                            onChange={(e) => handleFormChange(id, e.target.value)}
                            className={`${inputBase} appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20fill%3D%22%236B6B6B%22%20d%3D%22M7%207l3%203%203-3%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px] bg-[right_12px_center] bg-no-repeat pr-10${lockedFields.has(id) ? ' opacity-60 cursor-not-allowed' : ''}`}
                            required={isPublicMode && REQUIRED_PUBLIC_FIELDS.has(id)}
                            disabled={lockedFields.has(id)}
                        >
                            <option value="">{t('admin.monday.select_option')}</option>
                            {options.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                )
            }
            case 'location': {
                const suggestions = locationSuggestions[id] || []
                const locationMaxLength = FIELD_MAX_LENGTHS[id]

                return (
                    <div key={id} className={fieldWrapper}>
                        <label className={labelBase}>
                            <FieldIcon className={iconColor} />
                            {label}
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={typeof formData[id] === 'object' ? formData[id].address : formData[id] || ''}
                                onChange={(e) => handleLocationInput(id, e.target.value)}
                                onFocus={() => newSessionToken()}
                                className={inputBase}
                                placeholder="0000 NW 1st Ave, Miami, FL 33000"
                                autoComplete="off"
                                maxLength={locationMaxLength}
                            />
                            {suggestions.length > 0 && (
                                <ul className="absolute z-20 w-full mt-2 overflow-hidden rounded-xl border border-gray-700 bg-[#0f1115] shadow-2xl">
                                    {suggestions.map(suggestion => (
                                        <li
                                            key={suggestion.id}
                                            onMouseDown={(e) => {
                                                e.preventDefault()
                                                handleLocationSuggestionSelect(id, suggestion)
                                            }}
                                            className="px-4 py-3 text-sm text-white cursor-pointer hover:bg-white/5"
                                        >
                                            <p className="font-semibold">{suggestion.label}</p>
                                            {suggestion.secondary && (
                                                <p className="text-xs text-gray-400">{suggestion.secondary}</p>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )
            }
            case 'email':
                const emailMaxLength = FIELD_MAX_LENGTHS[id]
                
                return (
                    <div key={id} className={fieldWrapper}>
                        <label className={labelBase}>
                            <FieldIcon className={iconColor} />
                            {label}
                        </label>
                        <input
                            type="email"
                            value={formData[id] || ''}
                            onChange={(e) => handleFormChange(id, e.target.value)}
                            className={inputBase}
                            placeholder="email@example.com"
                            maxLength={emailMaxLength}
                        />
                    </div>
                )
            case 'phone':
                return (
                    <div key={id} className={fieldWrapper}>
                        <label className={labelBase}>
                            <FieldIcon className={iconColor} />
                            {label}
                            {isPublicMode && REQUIRED_PUBLIC_FIELDS.has(id) && <span className="text-red-400">*</span>}
                        </label>
                        <input
                            type="tel"
                            value={formatPhoneNumber(formData[id] || '')}
                            onChange={(e) => handlePhoneChange(id, e.target.value)}
                            className={inputBase}
                            placeholder="(305) 555-1234"
                            maxLength={25}
                            required={isPublicMode && REQUIRED_PUBLIC_FIELDS.has(id)}
                        />
                    </div>
                )
            case 'date':
                return (
                    <div key={id} className={fieldWrapper}>
                        <label className={labelBase}>
                            <FieldIcon className={iconColor} />
                            {label}
                        </label>
                        <input
                            type="date"
                            value={formData[id] || ''}
                            onChange={(e) => handleFormChange(id, e.target.value)}
                            className={inputBase}
                        />
                    </div>
                )
            default:
                return null
        }
    }

    // --- CREATE APPLICATION FORM ---
    async function openCreateForm(formType = 'estimados_2026_new_app') {
        const formConfig = WORK_FORMS[formType]
        if (!formConfig) return

        setActiveFormType(formType)
        setLoading(true)
        setFormData({}) // Reset form data
        setUploadingFiles([])
        setDraftPrompt(null)

        try {
            // Fetch columns for this specific board (skip for landing forms — they go GHL-only)
            if (!isLandingMode) {
                const columnsData = await fetchBoardColumns(formConfig.boardId, session)
                setBoardColumns(columnsData?.columns || [])
            }

            // Build initial form values (including any preselected fields from landing pages)
            const seedData = { name: '' }
            if (initialPreselect?.type) seedData['color_mktck81k'] = initialPreselect.type
            if (initialPreselect?.desc) seedData['color_mktcap3t'] = initialPreselect.desc
            if (isLandingMode) {
                // Auto-fill hidden fields
                seedData['date_mkwae5gk'] = new Date().toISOString().split('T')[0]
                seedData['dropdown_mkt9162r'] = navigator.language?.startsWith('es') ? 'Español' : 'English'
            }

            // Check for saved draft (scoped to current user)
            const draft = await loadDraft(formType, draftUserId).catch(() => null)
            const draftHasRealData = draft?.formData && Object.entries(draft.formData).some(([k, v]) => {
                if (v === '' || v === null || v === undefined) return false
                if (isLandingMode && LANDING_SEED_KEYS.has(k)) return false
                return true
            })
            if (draftHasRealData) {
                // Show draft resume prompt — don't auto-restore
                setDraftPrompt(draft)
                setFormData(seedData) // Start fresh until user decides
            } else {
                setFormData(seedData)
            }

            setView('create')
        } catch (err) {
            console.error('Error fetching columns for form:', err)
            showToast('Failed to load form structure', 'error')
        } finally {
            setLoading(false)
        }
    }

    function restoreDraft() {
        if (!draftPrompt) return
        setFormData(draftPrompt.formData)
        setUploadingFiles(draftPrompt.files || [])
        setDraftPrompt(null)
        showToast(t('admin.monday.draft_restored', 'Draft restored'), 'success')
    }

    function discardDraft() {
        if (activeFormType) deleteDraft(activeFormType, draftUserId).catch(() => {})
        setDraftPrompt(null)
    }

    function handleFormChange(field, value) {
        if (lockedFields.has(field)) return
        setFormData(prev => {
            const next = { ...prev, [field]: value }
            // Type changes → reset Description + Option (skip if locked)
            if (field === 'color_mktck81k') {
                if (!lockedFields.has('color_mktcap3t')) next['color_mktcap3t'] = ''
                next['_option_subcategory'] = ''
            }
            // Description changes → reset Option
            if (field === 'color_mktcap3t') {
                next['_option_subcategory'] = ''
            }
            return next
        })
    }

    // Format phone number for client-side display
    const formatPhoneNumber = (value) => {
        if (!value) return value
        
        // Remove all non-numeric characters
        let cleaned = value.replace(/\D/g, '')
        
        // Handle country code (remove leading 1 for US numbers)
        if (cleaned.length > 10 && cleaned.startsWith('1')) {
            cleaned = cleaned.slice(1)
        }
        
        // Apply US phone number formatting
        if (cleaned.length === 0) return ''
        if (cleaned.length <= 3) return cleaned
        if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`
        if (cleaned.length <= 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
        
        // Handle numbers longer than 10 digits
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)} ${cleaned.slice(10)}`
    }

    // Handle phone input with formatting
    const handlePhoneChange = (field, value) => {
        // Remove all non-numeric characters
        let cleanValue = value.replace(/\D/g, '')
        // Remove leading 1 for US numbers to avoid issues
        if (cleanValue.length > 10 && cleanValue.startsWith('1')) {
            cleanValue = cleanValue.slice(1)
        }
        // Limit to 15 digits (E.164 international maximum)
        cleanValue = cleanValue.slice(0, 15)
        // Store the clean value in form state for display
        setFormData(prev => ({ ...prev, [field]: cleanValue }))
    }

    function handleLocationInput(field, value) {
        handleFormChange(field, value)

        if (!value.trim()) {
            setLocationSuggestions(prev => ({ ...prev, [field]: [] }))
            return
        }

        if (locationDebounceRef.current[field]) {
            clearTimeout(locationDebounceRef.current[field])
        }

        locationDebounceRef.current[field] = setTimeout(async () => {
            try {
                const suggestions = await fetchAddressSuggestions(value.trim())
                setLocationSuggestions(prev => ({ ...prev, [field]: suggestions }))
            } catch (error) {
                console.error('Location autocomplete error:', error)
                setLocationSuggestions(prev => ({ ...prev, [field]: [] }))
            }
        }, 350)
    }

    async function handleLocationSuggestionSelect(field, suggestion) {
        const details = suggestion.placeId ? await getPlaceDetails(suggestion.placeId) : null
        if (details) {
            const locationValue = (details.lat != null && details.lng != null)
                ? { lat: details.lat, lng: details.lng, address: details.address }
                : details.address
            handleFormChange(field, locationValue)
            // Auto-fill zip in landing mode
            if (isLandingMode && details.zipCode) {
                handleFormChange('_landing_zip', details.zipCode)
            }
        } else {
            const address = suggestion.value || suggestion.label
            handleFormChange(field, address)
        }
        setLocationSuggestions(prev => ({ ...prev, [field]: [] }))
        newSessionToken()
    }

    function handleFileSelect(e) {
        const files = Array.from(e.target.files || [])
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'video/mp4']
        const maxSize = 50 * 1024 * 1024 // 50MB

        const validFiles = files.filter(file => {
            const isValidType = allowedTypes.includes(file.type) || 
                               file.name.toLowerCase().match(/\.(png|jpe?g|pdf|docx|mp4)$/)
            const isValidSize = file.size <= maxSize

            if (!isValidType) {
                showToast(t('admin.monday.file_format_error', { fileName: file.name }), 'error')
                return false
            }
            if (!isValidSize) {
                showToast(t('admin.monday.file_size_error', { fileName: file.name }), 'error')
                return false
            }

            return true
        })

        if (validFiles.length > 0) {
            setUploadingFiles(prev => [...prev, ...validFiles])
        }
    }

    function removeFile(index) {
        setUploadingFiles(prev => prev.filter((_, i) => i !== index))
    }

    async function handleSubmitApplication(e) {
        e.preventDefault()

        // --- Frontend validation (public mode) ---
        if (isPublicMode) {
            const errors = []
            const formConfig = WORK_FORMS[activeFormType] || WORK_FORMS.estimados_2026_new_app
            const cols = isLandingMode ? LANDING_VISIBLE_COLUMNS : formConfig.visibleColumns

            if (!isLandingMode && !formData.name?.trim()) errors.push(t('admin.monday.project_address'))

            if (isLandingMode && !formData['_landing_zip']?.trim())
                errors.push(t('admin.monday.landing_zip_code', 'Zip Code'))

            if (cols.includes('color_mktck81k') && !formData['color_mktck81k'])
                errors.push(t('admin.monday.project_type'))

            if (cols.includes('color_mktcap3t') && !formData['color_mktcap3t'])
                errors.push(t('admin.monday.project_description'))

            if (cols.includes('_option_subcategory') && formData['color_mktcap3t'] && !formData['_option_subcategory'])
                errors.push(t('admin.monday.option_subcategory', 'Option'))

            if (isLandingMode && !formData['_landing_timeline'])
                errors.push(t('admin.monday.landing_timeline_label', 'Project timeline'))

            if (cols.includes('text_mkt9adtr') && !formData['text_mkt9adtr']?.trim())
                errors.push(isLandingMode ? t('admin.monday.landing_full_name', 'Full Name') : t('admin.monday.client_name'))

            if (cols.includes('text_mkt9qv82')) {
                const email = (formData['text_mkt9qv82'] || '').trim()
                if (!isLandingMode && !email) errors.push(t('admin.monday.client_email'))
                else if (email && !EMAIL_REGEX.test(email)) {
                    showToast(t('admin.monday.invalid_email', 'Please enter a valid email address'), 'error')
                    return
                }
            }

            if (cols.includes('phone_mkt9n841')) {
                const digits = (formData['phone_mkt9n841'] || '').replace(/\D/g, '')
                if (!digits) errors.push(t('admin.monday.client_phone'))
                else if (digits.length < 10) {
                    showToast(t('admin.monday.invalid_phone', 'Phone number must be at least 10 digits'), 'error')
                    return
                }
            }

            if (errors.length > 0) {
                showToast(`${t('admin.monday.required_fields', 'Required')}: ${errors.join(', ')}`, 'error')
                return
            }
        }

        setSubmitting(true)

        try {
            // ── LANDING MODE: GHL-only submission (skip Monday) ──
            if (isLandingMode) {
                const zip = (formData['_landing_zip'] || '').trim()
                const addr = (formData.name || '').trim()
                const composedAddress = addr ? `${addr}, ${zip}` : zip

                // Build a shallow copy with composed address for GHL mapping
                const ghlFormData = { ...formData, name: composedAddress }

                const scope = (formData['long_text_mm1k8fb1'] || '').trim()
                const ghlResult = await syncFormToGHL({ formType: activeFormType, formData: ghlFormData, scopeText: scope, utmData })

                if (activeFormType) deleteDraft(activeFormType, draftUserId).catch(() => {})

                if (isPublicMode && thankYouPath) {
                    navigate(thankYouPath + (utmQuery ? (thankYouPath.includes('?') ? '&' : '?') + utmQuery : ''))
                } else {
                    setSuccessModal({ show: true, message: t('admin.monday.create_success') })
                }
                setFormData({ name: '' })
                setUploadingFiles([])
                return
            }

            const formConfig = WORK_FORMS[activeFormType] || WORK_FORMS.estimados_2026_new_app

            // Build column values dynamically
            const columnValues = {}

            boardColumns.forEach(column => {
                const value = formData[column.id]
                if (value === undefined || value === '' || value === null || column.id === 'name') return

                // Format value based on column type
                switch (column.type) {
                    case 'color':
                    case 'status':
                        if (value !== null && value !== '') {
                            const numIdx = Number(value)
                            if (!isNaN(numIdx)) {
                                // Numeric index (Architecture board Monday labels)
                                columnValues[column.id] = { index: numIdx }
                            } else {
                                // Text label (Construction/Roofing SUBCATEGORY_MAP values)
                                columnValues[column.id] = { label: value }
                            }
                        }
                        break
                    case 'dropdown': {
                        // Reject stale values (e.g. a saved draft with a renamed label) —
                        // create_labels_if_missing would silently create a stray label
                        let names = []
                        try { names = (JSON.parse(column.settings_str || '{}').labels || []).map(l => l.name) } catch { /* no options */ }
                        if (names.length && !names.includes(value)) {
                            console.warn('[submit] dropdown value not a board option, skipped:', column.id, value)
                            return
                        }
                        columnValues[column.id] = { labels: [value] }
                        break
                    }
                    case 'numeric':
                    case 'numbers':
                        columnValues[column.id] = parseFloat(value)
                        break
                    case 'phone':
                        // Strip all non-numeric characters for Monday.com compatibility
                        let cleanPhone = value.replace(/\D/g, '')
                        // Remove leading 1 for US numbers to avoid issues
                        if (cleanPhone.length > 10 && cleanPhone.startsWith('1')) {
                            cleanPhone = cleanPhone.slice(1)
                        }
                        columnValues[column.id] = { phone: cleanPhone, countryShortName: 'US' }
                        break
                    case 'location':
                        if (typeof value === 'object' && value.lat != null && value.lng != null) {
                            columnValues[column.id] = { lat: value.lat, lng: value.lng, address: value.address }
                        } else {
                            // Location fields require lat/lng, so skip if not provided
                            return
                        }
                        break
                    case 'date':
                        columnValues[column.id] = { date: value }
                        break
                    case 'long_text':
                        columnValues[column.id] = { text: value }
                        break
                    case 'link':
                        columnValues[column.id] = { url: value }
                        break
                    case 'boolean':
                        columnValues[column.id] = { checked: value === 'true' }
                        break
                    default:
                        // Default to direct assignment for text, email
                        columnValues[column.id] = value
                }
            })

            // Construction/Roofing/Windows: 3-column cascade
            // color_mktck81k (Tipo)        ← Type (Comercial/Residencial) — already set above
            // color_mktcap3t (Descripcion) ← Category  (from Description field) — by label
            // color_mm1mdv85 (Opción)      ← Subcategory (from Option field) — by label
            // Both rely on create_labels_if_missing:true in createItem mutation
            if (formConfig.boardId === '18157237865') {
                const category = formData['color_mktcap3t']
                const subcategory = formData['_option_subcategory']
                // Descripcion ← category label
                if (category) {
                    columnValues['color_mktcap3t'] = { label: category }
                }
                // Opción ← subcategory label
                if (subcategory) {
                    columnValues['color_mm1mdv85'] = { label: subcategory }
                }
            }

            // Resolve file column ID
            let fileColumn = boardColumns.find(c => c.type === 'file')?.id
            if (!fileColumn) {
                if (formConfig.boardId === '18396590426') {
                    fileColumn = 'file_mkt9257p' // Architecture board
                } else if (formConfig.boardId === '18157237865') {
                    fileColumn = 'file_mm1m5y0t' // Construction board
                }
            }

            // Item name: "Client Name | Address" convention
            // NOTE: This naming convention must match the Google Drive folder naming standard.
            //       If Dallas + Miami sub-accounts are consolidated, verify this still holds.
            const address = formData.name || t('admin.monday.new_application', 'New Application')
            const clientName = (formData['text_mkt9adtr'] || '').trim()
            const itemName = clientName ? `${clientName} | ${address}` : address

            // ── OFFLINE: Queue to outbox ──
            if (!navigator.onLine) {
                await addToOutbox({
                    formType: activeFormType,
                    boardId: formConfig.boardId,
                    groupId: formConfig.groupId,
                    itemName,
                    columnValues,
                    fileColumnId: fileColumn,
                    files: uploadingFiles,
                    formData: JSON.parse(JSON.stringify(formData))
                })

                // Register Background Sync (Chrome/Edge — ignored on Safari)
                try {
                    const reg = await navigator.serviceWorker?.ready
                    if (reg?.sync) await reg.sync.register('outbox-sync')
                } catch {}

                // Request notification permission for when sync completes
                requestNotificationPermission().catch(() => {})

                // Update outbox count
                const count = await getOutboxCount()
                setOutboxCount(count)

                // Clear draft
                if (activeFormType) deleteDraft(activeFormType, draftUserId).catch(() => {})

                if (isPublicMode && thankYouPath) {
                    navigate(thankYouPath + (utmQuery ? (thankYouPath.includes('?') ? '&' : '?') + utmQuery : ''))
                } else {
                    setSuccessModal({
                        show: true,
                        message: t('admin.monday.queued_offline', 'You are offline. Your form has been saved and will be submitted automatically when you reconnect.')
                    })
                }

                if (isPublicMode) {
                    setFormData({ name: '' })
                    setUploadingFiles([])
                }
                return // Skip online submission
            }

            // ── ONLINE: Submit normally ──
            const result = await createItem(
                formConfig.boardId,
                itemName,
                columnValues,
                formConfig.groupId,
                isPublicMode ? null : session
            )

            // Upload files if any
            if (uploadingFiles.length > 0 && result.id && fileColumn) {
                for (const file of uploadingFiles) {
                    try {
                        await uploadFile(formConfig.boardId, result.id, fileColumn, file, isPublicMode ? null : session)
                    } catch (fileErr) {
                        console.error('Error uploading file:', fileErr)
                    }
                }
            }

            // Fire-and-forget GHL sync (never blocks success flow)
            syncFormToGHL({ formType: activeFormType, formData, utmData }).catch(() => {})

            // Newly created client must be pickable on the next form
            clearMondayCache(buildMondayCacheKey('projects', session, formConfig.boardId))
            setClientItemsCache(prev => ({ ...prev, [formConfig.boardId]: undefined }))

            // Clear draft on success
            if (activeFormType) deleteDraft(activeFormType, draftUserId).catch(() => {})

            if (isPublicMode && thankYouPath) {
                navigate(thankYouPath + (utmQuery ? (thankYouPath.includes('?') ? '&' : '?') + utmQuery : ''))
            } else {
                setSuccessModal({ show: true, message: t('admin.monday.create_success') })
            }
            if (isPublicMode) {
                setFormData({ name: '' })
                setUploadingFiles([])
            }
        } catch (err) {
            console.error('Error creating item:', err)
            showToast(err.message || 'Failed to create application', 'error')
        } finally {
            setSubmitting(false)
        }
    }

    // --- RENDER ---

    // Loading state
    if (loading) {
        return (
            <div className="flex flex-col justify-center items-center min-h-screen bg-cloud">
                <FaSpinner className="mb-4 text-4xl animate-spin text-primary" />
                <p className="text-ash">{t('common.loading')}</p>
            </div>
        )
    }

    // Error state
    if (error && view === 'boards') {
        return (
            <div className="flex flex-col justify-center items-center p-8 min-h-screen text-center bg-cloud">
                <FaExclamationCircle className="mb-4 text-5xl text-red-500" />
                <h2 className="mb-2 text-2xl font-bold text-ink">{t('common.error')}</h2>
                <p className="mb-4 text-ash">{error}</p>
                <button
                    onClick={loadBoards}
                    className="px-6 py-2 font-medium rounded-lg shadow transition-all bg-primary text-ink hover:bg-primary/90"
                >
                    {t('common.retry', 'Retry')}
                </button>
            </div>
        )
    }

    // --- CREATE APPLICATION VIEW ---
    if (view === 'create') {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="min-h-screen bg-cloud"
            >
                {/* Header — hidden in landing mode (title provided by FormPageTemplate) */}
                {!isLandingMode && (
                <header className={`sticky ${isPublicMode ? 'top-0' : 'top-20'} z-40 bg-white border-b border-gray-200 shadow-sm`}>
                    <div className="flex gap-3 items-center px-4 py-3 mx-auto max-w-7xl">
                        <button
                            onClick={() => {
                                if (isPublicMode) {
                                    onReturn?.()
                                } else {
                                    setView('boards')
                                    setActiveFormType(null)
                                }
                            }}
                            className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-gray-100 text-ash shrink-0"
                            title={t('common.return')}
                        >
                            <FaArrowLeft />
                        </button>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-xl font-bold truncate text-primary">
                                {activeFormType === 'estimados_2026_new_app' && t('admin.monday.new_application')}
                                {activeFormType === 'estimados_2026_new_scope' && t('admin.monday.new_scope')}
                                {activeFormType === 'construction_new_project' && t('admin.monday.construction_new_project')}
                                {activeFormType === 'roofing_new_project' && t('admin.monday.roofing_new_project')}
                                {activeFormType === 'impact_windows_new_project' && t('admin.monday.impact_windows_new_project')}
                                {!activeFormType && t('admin.monday.new_application')}
                            </h1>
                            <p className="text-xs text-ash">{WORK_FORMS[activeFormType]?.boardName || 'Estimados 2026'}</p>
                        </div>
                    </div>
                    {!isPublicMode && (
                        <div className="px-4 pb-2 mx-auto max-w-7xl">
                            <ServiceHealthBar />
                        </div>
                    )}
                </header>
                )}

                {/* Form */}
                <form onSubmit={handleSubmitApplication} className="px-4 py-6 mx-auto max-w-4xl">

                    {/* Offline indicator */}
                    {!isOnline && (
                        <div className="flex gap-3 items-center p-3 mb-4 text-sm font-medium text-amber-800 bg-amber-50 rounded-lg border border-amber-200">
                            <span className="flex-shrink-0 w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                            {t('admin.monday.offline_notice', 'You are offline. Your form will be saved and submitted when you reconnect.')}
                        </div>
                    )}

                    {/* Outbox pending badge */}
                    {outboxCount > 0 && (
                        <div className="flex gap-3 items-center p-3 mb-4 text-sm font-medium text-blue-800 bg-blue-50 rounded-lg border border-blue-200">
                            <FaUpload className="flex-shrink-0 text-blue-500" />
                            {t('admin.monday.outbox_pending', {
                                count: outboxCount,
                                defaultValue: `${outboxCount} form(s) queued — will send when online`
                            })}
                        </div>
                    )}

                    {/* Draft resume prompt */}
                    <AnimatePresence>
                        {draftPrompt && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="flex flex-col gap-3 justify-between items-start p-4 mb-4 bg-emerald-50 rounded-lg border border-emerald-200 sm:flex-row sm:items-center"
                            >
                                <div className="text-sm text-emerald-800">
                                    <p className="font-semibold">{t('admin.monday.draft_found', 'Unsaved draft found')}</p>
                                    <p className="text-emerald-600">
                                        {t('admin.monday.draft_saved_at', 'Saved')}: {new Date(draftPrompt.savedAt).toLocaleString()}
                                        {draftPrompt.files?.length > 0 && ` · ${draftPrompt.files.length} file(s)`}
                                    </p>
                                </div>
                                <div className="flex flex-shrink-0 gap-2">
                                    <button
                                        type="button"
                                        onClick={restoreDraft}
                                        className="px-3 py-1.5 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition"
                                    >
                                        {t('admin.monday.draft_resume', 'Resume')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={discardDraft}
                                        className="px-3 py-1.5 text-sm font-medium text-emerald-700 bg-white border border-emerald-300 rounded-lg hover:bg-emerald-50 transition"
                                    >
                                        {t('admin.monday.draft_discard', 'Discard')}
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="p-6 mb-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                        <h2 className="mb-6 text-lg font-semibold text-ink">
                            {t('admin.monday.project_info', 'Form Information')}
                        </h2>

                        {/* Render columns filtered and ordered by WorkForm visibleColumns config */}
                        {(() => {
                            const formConfig = WORK_FORMS[activeFormType]
                            const visibleCols = isLandingMode ? LANDING_VISIBLE_COLUMNS : formConfig?.visibleColumns
                            // Fallback: no config → render all board columns
                            if (!visibleCols?.length) {
                                return boardColumns.map(column => renderDynamicField(column))
                            }

                            const selectClass = `${inputBase} appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20fill%3D%22%236B6B6B%22%20d%3D%22M7%207l3%203%203-3%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px] bg-[right_12px_center] bg-no-repeat pr-10`

                            const elements = []
                            for (const colId of visibleCols) {
                                if (colId.startsWith('file_')) continue

                                // ── Synthetic: Option (subcategories cascading from Description) ──
                                if (colId === '_option_subcategory') {
                                    const selectedDesc = formData['color_mktcap3t']
                                    const subOptions = selectedDesc
                                        ? ((isLandingMode ? LANDING_SUBCATEGORY_MAP[selectedDesc] : null) || SUBCATEGORY_MAP[selectedDesc] || [])
                                        : []
                                    elements.push(
                                        <div key="_option_subcategory" className={fieldWrapper}>
                                            <label className={labelBase}>
                                                <FaTag className="text-sky-500" />
                                                {(() => {
                                                    if (isLandingMode && initialPreselect?.desc) {
                                                        const labelMap = {
                                                            'Re-Roof': t('admin.monday.roof_type', 'Roof type'),
                                                            'Concrete': t('admin.monday.concrete_type', 'Select service'),
                                                            'Pergola': t('admin.monday.pergola_type', 'Pergola type')
                                                        }
                                                        return labelMap[initialPreselect.desc] || t('admin.monday.option_subcategory', 'Option')
                                                    }
                                                    return t('admin.monday.option_subcategory', 'Option')
                                                })()}
                                                {isPublicMode && <span className="text-red-400">*</span>}
                                            </label>
                                            <select
                                                value={formData['_option_subcategory'] || ''}
                                                onChange={(e) => handleFormChange('_option_subcategory', e.target.value)}
                                                className={selectClass}
                                                required={isPublicMode}
                                                disabled={!selectedDesc}
                                            >
                                                <option value="">{selectedDesc ? t('admin.monday.select_option') : t('admin.monday.select_description_first', 'Select a description first')}</option>
                                                {subOptions.map(opt => (
                                                    <option key={opt} value={opt}>
                                                        {t(`admin.monday.${getTranslationKey(opt)}`, opt)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )
                                    continue
                                }

                                // ── Synthetic: Landing Timeline (lead qualification) ──
                                if (colId === '_landing_timeline') {
                                    elements.push(
                                        <div key="_landing_timeline" className={fieldWrapper}>
                                            <label className={labelBase}>
                                                <FaCalendarCheck className="text-emerald-500" />
                                                {t('admin.monday.landing_timeline_label')}
                                                <span className="text-red-400">*</span>
                                            </label>
                                            <select
                                                value={formData['_landing_timeline'] || ''}
                                                onChange={(e) => handleFormChange('_landing_timeline', e.target.value)}
                                                className={selectClass}
                                                required
                                            >
                                                <option value="">{t('admin.monday.select_option')}</option>
                                                <option value="asap">{t('admin.monday.landing_timeline_asap')}</option>
                                                <option value="1-3months">{t('admin.monday.landing_timeline_1_3months')}</option>
                                                <option value="exploring">{t('admin.monday.landing_timeline_exploring')}</option>
                                            </select>
                                        </div>
                                    )
                                    continue
                                }

                                // ── Synthetic: Landing Address + Zip row ──
                                if (colId === '_landing_address_row') {
                                    const landingSuggestions = locationSuggestions['name'] || []
                                    elements.push(
                                        <div key="_landing_address_row" className="flex flex-col gap-3 sm:flex-row">
                                            <div className={`${fieldWrapper} flex-[7]`}>
                                                <label className={labelBase}>
                                                    <FaMapMarkerAlt className="text-red-500" />
                                                    {t('admin.monday.landing_address', 'Address')}
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={formData.name || ''}
                                                        onChange={(e) => handleLocationInput('name', e.target.value)}
                                                        onFocus={() => newSessionToken()}
                                                        className={inputBase}
                                                        placeholder={t('admin.monday.landing_address_placeholder', '1234 NW 1st Ave, Miami, FL')}
                                                        maxLength={FIELD_MAX_LENGTHS.name}
                                                        autoComplete="off"
                                                    />
                                                    {landingSuggestions.length > 0 && (
                                                        <ul className="absolute z-20 w-full mt-2 overflow-hidden rounded-xl border border-gray-700 bg-[#0f1115] shadow-2xl">
                                                            {landingSuggestions.map(suggestion => (
                                                                <li
                                                                    key={suggestion.id}
                                                                    onMouseDown={(e) => {
                                                                        e.preventDefault()
                                                                        handleLocationSuggestionSelect('name', suggestion)
                                                                    }}
                                                                    className="px-4 py-3 text-sm text-white cursor-pointer hover:bg-white/5"
                                                                >
                                                                    <p className="font-semibold">{suggestion.label}</p>
                                                                    {suggestion.secondary && (
                                                                        <p className="text-xs text-gray-400">{suggestion.secondary}</p>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={`${fieldWrapper} flex-[3]`}>
                                                <label className={labelBase}>
                                                    <FaMapSigns className="text-blue-500" />
                                                    {t('admin.monday.landing_zip_code', 'Zip Code')}
                                                    <span className="text-red-400">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData['_landing_zip'] || ''}
                                                    onChange={(e) => handleFormChange('_landing_zip', e.target.value)}
                                                    className={inputBase}
                                                    placeholder={t('admin.monday.landing_zip_placeholder', '33101')}
                                                    maxLength={10}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    )
                                    continue
                                }

                                // ── Real Monday column ──
                                const col = boardColumns.find(c => c.id === colId)
                                    // Landing mode: boardColumns is empty (no fetchBoardColumns call)
                                    // so we synthesize a column object so renderDynamicField still works
                                    || (isLandingMode ? { id: colId, title: colId, type: colId.startsWith('phone_') ? 'phone' : colId.startsWith('long_text_') ? 'long_text' : 'text' } : null)
                                if (col) {
                                    const rendered = renderDynamicField(col)
                                    if (rendered) elements.push(rendered)
                                }
                            }
                            return elements
                        })()}

                        {/* File Upload Section - hide for landing forms */}
                        {!isLandingMode && (
                        <div className="pt-6 mt-6 border-t border-gray-100">
                            <label className="block mb-2 text-sm font-medium text-ink">
                                {t('admin.monday.upload_files', 'Upload Supporting Documents')}
                            </label>
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className="p-8 text-center rounded-lg border-2 border-gray-200 border-dashed transition-all cursor-pointer hover:border-primary/50 hover:bg-gray-50"
                            >
                                <FaUpload className="mx-auto mb-3 text-3xl text-ash" />
                                <p className="font-medium text-ink">{t('admin.monday.click_upload')}</p>
                                <p className="mt-1 text-xs text-ash">PNG, JPG, JPEG, PDF, DOCX, MP4 (Max 50MB)</p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept=".png,.jpg,.jpeg,.pdf,.docx,.mp4"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                            </div>

                            {/* Uploaded files list */}
                            {uploadingFiles.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {uploadingFiles.map((file, index) => (
                                        <div key={index} className="flex justify-between items-center px-4 py-2 bg-gray-50 rounded-lg">
                                            <span className="text-sm text-ink">{file.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeFile(index)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <FaTimes />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-4">
                        <button
                            type="button"
                            onClick={() => {
                                if (isPublicMode) onReturn?.()
                                else setView('boards')
                            }}
                            className="flex-1 px-8 py-4 font-bold tracking-widest text-black uppercase bg-white rounded-lg border-2 border-gray-200 transition-all hover:bg-gray-50"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 px-8 py-4 font-bold tracking-widest text-black uppercase rounded-lg shadow-lg transition-all bg-primary hover:bg-yellow-400 disabled:opacity-50"
                        >
                            {submitting ? (
                                <FaSpinner className="inline mr-2 animate-spin" />
                            ) : null}
                            {t('common.submit')}
                        </button>
                    </div>
                </form>

                {/* Error Toast */}
                <AnimatePresence>
                    {toast.show && toast.type === 'error' && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 50 }}
                            className="fixed right-8 bottom-8 z-50"
                        >
                            <div className="flex gap-3 items-center px-6 py-4 text-red-900 bg-red-50 rounded-lg border border-red-200 shadow-xl">
                                <FaExclamationCircle />
                                <span className="font-medium">{toast.message}</span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Success Modal */}
                <AnimatePresence>
                    {successModal.show && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/50"
                            onClick={closeSuccessModal}
                        >
                            <motion.div
                                initial={{ scale: 0.85, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.85, opacity: 0 }}
                                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                                className="flex relative flex-col gap-6 items-center px-10 py-10 mx-4 w-full max-w-sm text-center bg-white rounded-2xl shadow-2xl"
                                onClick={e => e.stopPropagation()}
                            >
                                <div className="flex justify-center items-center w-20 h-20 bg-green-100 rounded-full">
                                    <FaCheckCircle className="text-5xl text-green-500" />
                                </div>
                                <div>
                                    <h3 className="mb-2 text-xl font-bold text-ink">{t('admin.monday.success_title')}</h3>
                                    <p className="text-sm text-gray-500">{successModal.message}</p>
                                </div>
                                <button
                                    onClick={closeSuccessModal}
                                    className="py-3 w-full font-semibold text-white rounded-xl transition-colors bg-primary hover:bg-primary/90"
                                >
                                    {t('common.close')}
                                </button>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        )
    }

    // --- ITEMS VIEW ---
    if (view === 'items' && selectedBoard) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="min-h-screen bg-cloud"
            >
                {/* Header */}
                <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                    <div className="flex gap-3 items-center px-4 py-3 mx-auto max-w-7xl">
                        <button
                            onClick={() => setView('boards')}
                            className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-gray-100 text-ash shrink-0"
                            title={t('admin.monday.boards')}
                        >
                            <FaArrowLeft />
                        </button>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-xl font-bold truncate text-primary">{selectedBoard.name}</h1>
                            <p className="text-xs text-ash">{selectedBoard.items_count} {t('admin.monday.items')}</p>
                        </div>
                        <button
                            onClick={onLogout}
                            className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-red-600 hover:text-white hover:border-red-600 text-ash shrink-0"
                            title={t('auth.signOut')}
                        >
                            <FaSignOutAlt />
                        </button>
                    </div>
                    <div className="px-4 pb-2 mx-auto max-w-7xl">
                        <ServiceHealthBar />
                    </div>
                </header>

                {/* Search */}
                <div className="px-4 py-6 mx-auto max-w-7xl">
                    <div className="relative">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-ash" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={t('admin.monday.search_items')}
                            className="py-3 pr-4 pl-12 w-full bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>
                </div>

                {/* Items List */}
                <div className="px-4 pb-8 mx-auto max-w-7xl">
                    {loadingItems && items.length === 0 ? (
                        <div className="flex flex-col justify-center items-center py-12">
                            <FaSpinner className="mb-4 text-3xl animate-spin text-primary" />
                            <p className="text-ash">{t('admin.monday.loading_items')}</p>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="py-12 text-center">
                            <FaThList className="mx-auto mb-4 text-4xl text-ash" />
                            <p className="text-ash">{t('admin.monday.no_items')}</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredItems.map((item) => {
                                const isExpanded = expandedItems.has(item.id)

                                return (
                                    <motion.div
                                        key={item.id}
                                        layout
                                        className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm"
                                    >
                                        {/* Item Header */}
                                        <div
                                            onClick={() => toggleItemExpand(item.id)}
                                            className="flex justify-between items-center px-4 py-4 transition-colors cursor-pointer sm:px-6 hover:bg-gray-50"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-base font-semibold truncate text-ink">
                                                    {item.name || t('admin.monday.untitled')}
                                                </h3>
                                                <div className="flex gap-3 items-center mt-1 text-sm text-ash">
                                                    {item.group && (
                                                        <span className="flex gap-1 items-center">
                                                            <FaLayerGroup className="text-xs" />
                                                            {item.group.title}
                                                        </span>
                                                    )}
                                                    <span>
                                                        {new Date(item.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex gap-4 items-center">
                                                {/* Show first few column values as badges */}
                                                <div className="hidden gap-2 items-center sm:flex">
                                                    {item.column_values?.slice(0, 3).map((cv) => {
                                                        if (!cv.text) return null
                                                        const color = STATUS_COLORS[cv.text]
                                                        return (
                                                            <span
                                                                key={cv.id}
                                                                className="px-2 py-1 text-xs rounded-full bg-gray-100 text-ink truncate max-w-[120px]"
                                                                style={color ? { backgroundColor: color + '20', color } : {}}
                                                            >
                                                                {cv.text}
                                                            </span>
                                                        )
                                                    })}
                                                </div>
                                                {isExpanded ? <FaChevronUp className="text-ash" /> : <FaChevronDown className="text-ash" />}
                                            </div>
                                        </div>

                                        {/* Expanded Content */}
                                        <AnimatePresence>
                                            {isExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="bg-gray-50 border-t border-gray-200"
                                                >
                                                    <div className="px-4 py-4 sm:px-6">
                                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                                            {item.column_values?.map((cv) => {
                                                                const fieldCfg = FIELD_CONFIG[cv.id]
                                                                const fallback = TYPE_FALLBACK_ICONS[cv.type] || { icon: FaFileAlt, color: 'text-ash' }
                                                                const Icon = fieldCfg?.icon || fallback.icon
                                                                const iconColor = fieldCfg?.color || fallback.color
                                                                const bgColor = STATUS_COLORS[cv.text]

                                                                return (
                                                                    <div key={cv.id} className="p-3 bg-white rounded-lg border border-gray-100">
                                                                        <div className="flex gap-2 items-center mb-1">
                                                                            <Icon className={`text-sm ${iconColor}`} />
                                                                            <span className="text-xs tracking-wide uppercase text-ash">
                                                                                {boardColumns.find(c => c.id === cv.id)?.title || cv.type}
                                                                            </span>
                                                                        </div>
                                                                        <p
                                                                            className="text-sm font-medium text-ink"
                                                                            style={bgColor ? { color: bgColor } : {}}
                                                                        >
                                                                            {cv.text || '—'}
                                                                        </p>
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>

                                                        {/* Creator info */}
                                                        {item.creator && (
                                                            <div className="flex gap-2 items-center pt-4 mt-4 text-sm border-t border-gray-100 text-ash">
                                                                <FaUser className="text-xs" />
                                                                <span>{t('admin.monday.created_by')}: {item.creator.name}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                )
                            })}

                            {/* Load More */}
                            {hasMore && (
                                <div className="pt-4 text-center">
                                    <button
                                        onClick={loadMoreItems}
                                        disabled={loadingItems}
                                        className="px-6 py-3 font-medium bg-white rounded-lg border-2 border-gray-200 transition-all hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        {loadingItems ? (
                                            <FaSpinner className="inline mr-2 animate-spin" />
                                        ) : null}
                                        {t('admin.monday.load_more')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Toast */}
                <AnimatePresence>
                    {toast.show && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 50 }}
                            className="fixed right-8 bottom-8 z-50"
                        >
                            <div className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-xl border ${toast.type === 'success'
                                ? 'bg-green-50 text-green-900 border-green-200'
                                : 'bg-red-50 text-red-900 border-red-200'
                                }`}>
                                {toast.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                                <span className="font-medium">{toast.message}</span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        )
    }

    // --- BOARDS LIST VIEW (Default) ---
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen bg-cloud"
        >
            {/* Header */}
            <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                <div className="flex gap-3 items-center px-4 py-3 mx-auto max-w-7xl">
                    <button
                        onClick={onReturn}
                        className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-gray-100 text-ash shrink-0"
                        title={t('common.return')}
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="flex-1 text-xl font-bold text-primary">{t('admin.monday.title')}</h1>
                    <button
                        onClick={onLogout}
                        className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-red-600 hover:text-white hover:border-red-600 text-ash shrink-0"
                        title={t('auth.signOut')}
                    >
                        <FaSignOutAlt />
                    </button>
                </div>
                <div className="px-4 pb-2 mx-auto max-w-7xl">
                    <ServiceHealthBar />
                </div>
            </header>

            {/* WorkForm Quick Actions */}
            <div className="px-4 py-6 mx-auto max-w-7xl">
                <h2 className="mb-4 text-sm font-semibold tracking-wide uppercase text-ash">{t('admin.monday.quick_forms')}</h2>
                <div className="grid grid-cols-1 gap-3 max-w-5xl sm:grid-cols-2 lg:grid-cols-5">
                    {/* Estimados 2026 - New Application */}
                    <button
                        onClick={() => openCreateForm('estimados_2026_new_app')}
                        className="p-4 text-left text-white bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-md transition-all hover:from-purple-600 hover:to-purple-700 hover:shadow-lg group"
                    >
                        <div className="flex gap-3 items-center mb-2">
                            <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-white/20">
                                <FaPlus className="text-lg" />
                            </div>
                            <FaChevronRight className="ml-auto opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                        <h3 className="text-sm font-semibold">{t('admin.monday.new_application')}</h3>
                        <p className="mt-1 text-xs text-white/70">{WORK_FORMS.estimados_2026_new_app.boardName}</p>
                    </button>

                    {/* Estimados 2026 - New Scope */}
                    <button
                        onClick={() => openCreateForm('estimados_2026_new_scope')}
                        className="p-4 text-left text-white bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-md transition-all hover:from-indigo-600 hover:to-indigo-700 hover:shadow-lg group"
                    >
                        <div className="flex gap-3 items-center mb-2">
                            <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-white/20">
                                <FaClipboard className="text-lg" />
                            </div>
                            <FaChevronRight className="ml-auto opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                        <h3 className="text-sm font-semibold">{t('admin.monday.new_scope')}</h3>
                        <p className="mt-1 text-xs text-white/70">{WORK_FORMS.estimados_2026_new_scope.boardName}</p>
                    </button>

                    {/* Construction - Unified Intake */}
                    <button
                        onClick={() => openCreateForm('construction_new_project')}
                        className="p-4 text-left text-white bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-md transition-all hover:from-emerald-600 hover:to-emerald-700 hover:shadow-lg group"
                    >
                        <div className="flex gap-3 items-center mb-2">
                            <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-white/20">
                                <FaHardHat className="text-lg" />
                            </div>
                            <FaChevronRight className="ml-auto opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                        <h3 className="text-sm font-semibold">{t('admin.monday.construction_new_project')}</h3>
                        <p className="mt-1 text-xs text-white/70">{WORK_FORMS.construction_new_project.boardName}</p>
                    </button>

                    {/* Roofing - New Project */}
                    <button
                        onClick={() => openCreateForm('roofing_new_project')}
                        className="p-4 text-left text-white bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-md transition-all hover:from-amber-600 hover:to-amber-700 hover:shadow-lg group"
                    >
                        <div className="flex gap-3 items-center mb-2">
                            <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-white/20">
                                <FaHome className="text-lg" />
                            </div>
                            <FaChevronRight className="ml-auto opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                        <h3 className="text-sm font-semibold">{t('admin.monday.roofing_new_project')}</h3>
                        <p className="mt-1 text-xs text-white/70">{WORK_FORMS.roofing_new_project.boardName}</p>
                    </button>

                    {/* Impact Windows - New Project */}
                    <button
                        onClick={() => openCreateForm('impact_windows_new_project')}
                        className="p-4 text-left text-white bg-gradient-to-br from-sky-500 to-sky-600 rounded-xl shadow-md transition-all hover:from-sky-600 hover:to-sky-700 hover:shadow-lg group"
                    >
                        <div className="flex gap-3 items-center mb-2">
                            <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-white/20">
                                <FaWindowMaximize className="text-lg" />
                            </div>
                            <FaChevronRight className="ml-auto opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                        <h3 className="text-sm font-semibold">{t('admin.monday.impact_windows_new_project')}</h3>
                        <p className="mt-1 text-xs text-white/70">{WORK_FORMS.impact_windows_new_project.boardName}</p>
                    </button>
                </div>
            </div>

            {/* Search + Boards Grid — hidden while SHOW_BOARDS is false */}
            {SHOW_BOARDS && (<>
            <div className="px-4 pb-6 mx-auto max-w-7xl">
                <h2 className="mb-4 text-sm font-semibold tracking-wide uppercase text-ash">{t('admin.monday.browse_boards')}</h2>
                <div className="relative">
                    <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-ash" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('admin.monday.search_boards')}
                        className="py-3 pr-4 pl-12 w-full bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                </div>
            </div>

            {/* Boards Grid */}
            <div className="px-4 pb-8 mx-auto max-w-7xl">
                {filteredBoards.length === 0 ? (
                    <div className="py-12 text-center">
                        <FaThList className="mx-auto mb-4 text-4xl text-ash" />
                        <p className="text-ash">{t('admin.monday.no_boards')}</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {filteredBoards.map((board) => (
                            <motion.div
                                key={board.id}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => selectBoard(board)}
                                className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm transition-all cursor-pointer hover:shadow-md hover:border-primary/30"
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex justify-center items-center w-12 h-12 rounded-lg bg-primary/10">
                                        <FaThList className="text-xl text-primary" />
                                    </div>
                                    <FaChevronRight className="text-ash" />
                                </div>

                                <h3 className="mb-2 text-lg font-semibold text-ink">{board.name}</h3>

                                {board.description && (
                                    <p className="mb-4 text-sm text-ash line-clamp-2">{board.description}</p>
                                )}

                                <div className="flex gap-4 items-center text-sm text-ash">
                                    <span className="flex gap-1 items-center">
                                        <FaLayerGroup />
                                        {board.items_count} {t('admin.monday.items')}
                                    </span>
                                    <span className="flex gap-1 items-center">
                                        <FaThList />
                                        {board.columns_count} {t('admin.monday.columns')}
                                    </span>
                                </div>

                                {board.workspace && (
                                    <div className="pt-3 mt-3 text-xs border-t border-gray-100 text-ash">
                                        {board.workspace.name}
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
            </>)}

            {/* Toast */}
            <AnimatePresence>
                {toast.show && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="fixed right-8 bottom-8 z-50"
                    >
                        <div className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-xl border ${toast.type === 'success'
                            ? 'bg-green-50 text-green-900 border-green-200'
                            : 'bg-red-50 text-red-900 border-red-200'
                            }`}>
                            {toast.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                            <span className="font-medium">{toast.message}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}

export default MondayManagement
